import {css} from '@emotion/react'
import {useMapContext} from '@/components/MindMap/index.mjs'
import {useModel} from '../../bizNodeModel.mjs'
import {useProperties} from './Properties.mjs'

const cssLabelWrapper = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
})

const cssLabel = css({
    '&[data-required="1"]::before': {
        content: '"*"',
        color: '#ff4d4f',
        marginRight: 4,
    }
})

const Label = ({
    children,
    required,
    suffix,
    ...props
}) => {
    return (
        <div css={cssLabelWrapper}>
            <div
                css={cssLabel}
                data-required={required ? '1' : '0'}
                {...props}
            >
                {children}
            </div>

            {suffix}
        </div>
    )
}

const cssController = css({
    display: 'flex',
    flexDirection: 'column',
})

const cssControllerInline = css({
    flexDirection: 'row',
    gap: 8,
})

const cssErrorMessage = css({
    color: '#ff4d4f',

    '&:empty': {
        display: 'none',
    },
})

export function PropertiesController({
    children,
    error,
    hidden,
    inline,
    label,
    labelSuffix,
    required,
    ...props
}) {
    if (hidden) {
        return null
    }

    const onKeyDown = e => e.stopPropagation()
    const csss = [cssController]

    if (inline) {
        csss.push(cssControllerInline)
    }

    return (
        <div
            css={csss}
            onKeyDown={onKeyDown}
            {...props}
        >
            <Label
                required={required}
                suffix={labelSuffix}
            >
                {label}
            </Label>

            {children}

            <div css={cssErrorMessage}>
                {error}
            </div>
        </div>
    )
}

export const usePropertiesController = ({
    disabled,
    hidden,
    label,
    prop,
    readOnly,
    required,
    value,
}) => {
    const map = useMapContext()
    const nodes = [...map.useSelectedNodes()]
    const [values, update] = useProperties()
    const Model = useModel()

    const p = {
        disabled,
        hidden,
        label,
        prop,
        readOnly,
        required,
        value,
    }

    if (prop) {
        const model = new Model(map)
        p.disabled ??= nodes.some(n => model.disabled(n, prop))
        p.error = model.validate({data: values}, prop)
        p.hidden ??= nodes.some(n => model.hidden(n, prop))
        p.label ??= model.name(nodes[0], prop)
        p.readOnly ??= nodes.some(n => model.readOnly(n, prop))
        p.required ??= nodes.some(n => model.required(n, prop))
        p.value = values[prop]

        if (! (p.disabled || p.hidden || p.readOnly)) {
            p.onChange = newValue => {
                const oldValue = values[prop]

                if (undefined === oldValue || null === oldValue) {
                    if (newValue || 0 === newValue) {
                        update({[prop]: newValue})
                    }
                }
                else {
                    if (oldValue !== newValue) {
                        update({[prop]: newValue})
                    }
                }
            }
        }
    }

    if (! (p.disabled || p.hidden || p.readOnly)) {
        const onBlur = () => map.finishTask()
        const onFocus = () => map.startTask()
        Object.assign(p, {onBlur, onFocus})
    }

    return p
}
